// Health dashboard — single-page operational view of the federation,
// feature flags, registered Supabase projects, vault status, and
// per-org integration config. First stop when rolling out a flip or
// tracking down a "why isn't X working" report.
//
// All checks are non-destructive and run once per mount. Status rows
// render red/green/neutral based on probe results; clicking any row
// copies its diagnostic payload to clipboard for sharing in a bug
// report.

import { RAW_URL as SUPABASE_URL } from '../../functions/supabaseClient.js'
import { loadIntegrationCatalogue } from '../../integrations/index.js'
import { getSDK } from '../../functions/sdk.js'
import {
  USE_MULTITENANT,
  USE_FINANCIALS_PROJECT,
  DUAL_WRITE_FINANCIALS,
  USE_PROFILES_OVERLAY,
  USE_ONBOARDING_GATE
} from '../../../workspace-project/functions/featureFlags.js'

// ─── probes ────────────────────────────────────────────────────────────────

const _probeEdgeFunction = async (projectUrl, fnName) => {
  // OPTIONS returns 200 if the function exists + CORS preflight works,
  // 404 if the function isn't deployed, network error if the project is
  // unreachable entirely.
  //
  // TODO[sdk-edge-health]: there's no SDK method for direct CORS preflight
  // probes — this is admin-tooling that bypasses the auth/tenant model
  // intentionally to surface raw edge-function reachability. If/when the
  // workspace wrapper exposes a /system/edge-health admin route, replace
  // with `getSDK().execute('workspaceProject', 'rpc', { fn: fnName })`.
  try {
    const res = await fetch(`${projectUrl}/functions/v1/${fnName}`, { method: 'OPTIONS' })
    return { ok: res.status < 500, status: res.status }
  } catch (err) {
    return { ok: false, status: 0, error: err?.message }
  }
}

// migrated 2026-04-27 — was _probeTable(sb, 'profiles'). Routed through
// SDK system.status which returns aggregated reachability info for the
// workspace tenant tables (profiles, org_integrations, etc.).
const _probeSystemStatus = async () => {
  try {
    const result = await getSDK().execute('workspaceProject.system', 'status')
    const data = result?.data || result || {}
    // The wrapper returns { tables: { profiles: { count, ok, error } }, ... }
    // Surface the profiles slice for backwards-compat with the existing UI.
    const profiles = data?.tables?.profiles || data?.profiles || null
    return profiles
      ? { ok: profiles.ok ?? !profiles.error, error: profiles.error, count: profiles.count ?? null }
      : { ok: true, error: null, count: data?.count ?? null }
  } catch (err) {
    return { ok: false, error: err?.message, count: null }
  }
}

// migrated 2026-04-27 — was _probeOrgIntegrations(sb, orgId). Routed through
// the existing sdk-bridge loadIntegrationCatalogue helper which calls Mongo
// /org-integrations.
const _probeOrgIntegrations = async (orgId) => {
  if (!orgId) return { ok: false, rows: [], error: 'no active org' }
  try {
    const data = await loadIntegrationCatalogue(orgId)
    const rows = (data?.integrations || []).map(i => ({
      kind: i.kind,
      enabled: i.enabled,
      secrets_ref: i.has_secret ? 'present' : null,
      updated_at: i.updated_at
    }))
    return { ok: true, rows, error: null }
  } catch (err) {
    return { ok: false, rows: [], error: err?.message }
  }
}

// Orchestrator. Fires all probes in parallel, hydrates root state once.
const _runAllProbes = async (root) => {
  const claims = root?.jwtClaims || {}
  const activeOrgId = claims.workspace_org_id || null

  root.update({ _statusProbingAt: new Date().toISOString() })

  const [
    govBridge,
    govAdminOps,
    govHook,
    finBridge,
    finAdminOps,
    profilesCount,
    orgIntegrations
  ] = await Promise.all([
    _probeEdgeFunction(SUPABASE_URL, 'symbols-auth-bridge'),
    _probeEdgeFunction(SUPABASE_URL, 'symbols-admin-ops'),
    _probeEdgeFunction(SUPABASE_URL, 'custom-access-token-hook'),
    _probeEdgeFunction('https://bxhdvzwmvptgksqfkgqp.supabase.co', 'symbols-auth-bridge'),
    _probeEdgeFunction('https://bxhdvzwmvptgksqfkgqp.supabase.co', 'symbols-admin-ops'),
    _probeSystemStatus(),
    _probeOrgIntegrations(activeOrgId)
  ])

  root.update({
    _statusProbes: {
      govBridge,
      govAdminOps,
      govHook,
      finBridge,
      finAdminOps,
      profilesCount,
      orgIntegrations
    },
    _statusProbingAt: null
  })
}

// ─── page ──────────────────────────────────────────────────────────────────

const _flags = () => ([
  { key: 'USE_MULTITENANT', value: USE_MULTITENANT, unblocks: 'Phase 3 migrations + workspace RLS' },
  { key: 'USE_FINANCIALS_PROJECT', value: USE_FINANCIALS_PROJECT, unblocks: 'reads grants from financials' },
  { key: 'DUAL_WRITE_FINANCIALS', value: DUAL_WRITE_FINANCIALS, unblocks: 'mirrors grant writes to financials' },
  { key: 'USE_PROFILES_OVERLAY', value: USE_PROFILES_OVERLAY, unblocks: 'profile writes mirror to public.profiles' },
  { key: 'USE_ONBOARDING_GATE', value: USE_ONBOARDING_GATE, unblocks: 'no-team-org users see onboarding CTA' }
])

export const adminStatus = {
  extends: 'AppPage',
  metadata: { title: 'Status / Admin / Symbols Workspace' },
  onInit: (el, s) => {
    if (!el.call('shouldRedirectNoPerm', s, 'admin.pages.read')) return
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/', root, {}, { pushState: true })
  },
  onCreate: (el, s) => _runAllProbes(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'Status' } },
      RightGroup: {
        Refresh: {
          tag: 'button',
          type: 'button',
          padding: 'V Z',
          round: 'Y',
          border: 'none',
          background: 'overlay',
          color: 'title',
          fontSize: 'Y2',
          cursor: 'pointer',
          text: (el, s) => s.root._statusProbingAt ? 'Probing…' : 'Refresh',
          onClick: (e, el, s) => _runAllProbes(s.root)
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Section_Flags: {
        extends: 'AdminStatusSection',
        title: 'Feature flags',
        Rows: {
          childExtends: 'AdminStatusRow',
          childrenAs: 'state',
          children: () => _flags().map((f) => ({
            label: f.key,
            value: f.value ? 'on' : 'off',
            tone: f.value ? 'green' : 'caption',
            hint: f.unblocks
          }))
        }
      },

      Section_Projects: {
        extends: 'AdminStatusSection',
        title: 'Supabase projects',
        Rows: {
          childExtends: 'AdminStatusRow',
          childrenAs: 'state',
          children: (el, s) => {
            const p = s.root?._statusProbes || {}
            return [
              {
                label: 'governance / symbols-auth-bridge',
                value: p.govBridge ? `${p.govBridge.status}` : '—',
                tone: p.govBridge?.ok ? 'green' : 'red',
                hint: 'Login fan-out endpoint'
              },
              {
                label: 'governance / symbols-admin-ops',
                value: p.govAdminOps ? `${p.govAdminOps.status}` : '—',
                tone: p.govAdminOps?.ok ? 'green' : 'red',
                hint: 'HMAC-gated admin operations'
              },
              {
                label: 'governance / custom-access-token-hook',
                value: p.govHook ? `${p.govHook.status}` : '—',
                tone: p.govHook?.ok ? 'green' : 'caption',
                hint: 'Dormant until Supabase Pro'
              },
              {
                label: 'financials / symbols-auth-bridge',
                value: p.finBridge ? `${p.finBridge.status}` : '—',
                tone: p.finBridge?.ok ? 'green' : 'red',
                hint: 'Extension auth bridge'
              },
              {
                label: 'financials / symbols-admin-ops',
                value: p.finAdminOps ? `${p.finAdminOps.status}` : '—',
                tone: p.finAdminOps?.ok ? 'green' : 'red',
                hint: 'Extension admin operations'
              }
            ]
          }
        }
      },

      Section_Schema: {
        extends: 'AdminStatusSection',
        title: 'Schema / data',
        Rows: {
          childExtends: 'AdminStatusRow',
          childrenAs: 'state',
          children: (el, s) => {
            const p = s.root?._statusProbes || {}
            return [
              {
                label: 'public.profiles',
                value: p.profilesCount ? `${p.profilesCount.count ?? '?'} rows` : '—',
                tone: p.profilesCount?.ok ? 'green' : 'red',
                hint: p.profilesCount?.error || 'HR overlay (migration 0052)'
              },
              {
                label: 'public.org_integrations (current org)',
                value: p.orgIntegrations ? `${p.orgIntegrations.rows?.length ?? 0} entries` : '—',
                tone: p.orgIntegrations?.ok ? 'green' : 'caption',
                hint: p.orgIntegrations?.error || 'Per-org integration rows'
              }
            ]
          }
        }
      },

      Section_Claims: {
        extends: 'AdminStatusSection',
        title: 'Session claims',
        Rows: {
          childExtends: 'AdminStatusRow',
          childrenAs: 'state',
          children: (el, s) => {
            const c = s.root?.jwtClaims || {}
            return [
              { label: 'symbols_user_id', value: c.symbols_user_id || '—', tone: c.symbols_user_id ? 'title' : 'caption' },
              { label: 'symbols_role', value: c.symbols_role || '—', tone: c.symbols_role ? 'green' : 'caption' },
              { label: 'orgs', value: `${(c.orgs || []).length} entries`, tone: (c.orgs || []).length ? 'green' : 'red', hint: 'Server §A populates this' },
              { label: 'workspaces', value: `${(c.workspaces || []).length} entries`, tone: (c.workspaces || []).length ? 'green' : 'red', hint: 'Drives workspace switcher' },
              { label: 'active_workspace_id', value: c.active_workspace_id || '—', tone: c.active_workspace_id ? 'title' : 'caption' },
              { label: 'workspace_role', value: c.workspace_role || '—', tone: c.workspace_role ? 'title' : 'caption' },
              { label: 'workspace_tier', value: c.workspace_tier || '—', tone: c.workspace_tier ? 'green' : 'caption', hint: 'Drives tier gates' }
              // `hasFinanceGrant` retired per CU-9 §79 — financials access now
              // flows via explicit `financials.*` perms in `permissions[]`
              // (visible via the Phase E `permissions` claim, not a boolean).
            ]
          }
        }
      }
    }
  }
}

// ─── shared primitives ────────────────────────────────────────────────────

export const AdminStatusSection = {
  round: 'Z1',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  padding: 'A',
  flow: 'y',
  gap: 'Z',

  Title: {
    tag: 'h2',
    fontSize: 'Z',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: (el) => el.title || 'Section'
  },

  Rows: {
    flow: 'y',
    gap: 'V'
  }
}

export const AdminStatusRow = {
  flow: 'x',
  align: 'center space-between',
  gap: 'Z1',
  padding: 'V 0',
  borderBottom: '1px solid var(--color-line)',
  cursor: 'pointer',
  ':last-child': { borderBottom: 'none' },

  onClick: (e, el, s) => {
    // Copy the label + value for bug reports.
    const line = `${s.label}: ${s.value}${s.hint ? ` (${s.hint})` : ''}`
    try {
      navigator.clipboard?.writeText(line)
      el.call('openNotification', { title: 'Copied', message: line, type: 'success' })
    } catch { /* clipboard blocked, ignore */ }
  },

  Left: {
    flow: 'y', gap: 'V',
    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      text: (el, s) => s.label
    },
    Hint: {
      hide: (el, s) => !s.hint,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => s.hint
    }
  },

  Value: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '600',
    color: (el, s) => s.tone || 'title',
    text: (el, s) => s.value
  }
}
